/* eslint-disable complexity */
import * as React from 'react';
import cx from 'classnames';
import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {getTopicItemlist, TopicModule} from '@/services/pages/channel/channel.client';
import {useSearchParams} from '@/hooks/router';
import useJumpUrl from '@/hooks/useJumpUrl';
import {VALUE_TYPE} from '@/dicts/consts';
import styles from './index.less';

interface Props {
    page: string;
    sfRef?: string;
    infos: TopicModule;
    channelBusinessLine?: string;
    onMoreClick?: () => void;
}

interface ItemQuery {
    id?: string;
    moduleId?: number;
    pageSize: number;
    pageNo: number;
    sf_ch?: string;
    preview?: boolean;
}

const SelfTopicContentFlowBig = ({
    infos, onMoreClick, page, channelBusinessLine,
}: Props) => {

    const [size, setSize] = useState<number>(0);
    const sizeRef = useRef<number>(0);

    const [module, setModule] = useState<TopicModule>();
    const [itemQuery, setItemQuery] = useState<ItemQuery>({pageSize: 20, pageNo: 1});

    const param = useSearchParams();

    useEffect(
        () => {
            setModule(infos);
            const size = infos.moduleMaxRows || infos.itemList?.length || 0;
            const maxSize = size < (infos.itemList?.length || 0) ? size : (infos.itemList?.length || 0);

            sizeRef.current === 0 ? setSize(maxSize) : null;
            sizeRef.current === 0 ? sizeRef.current = maxSize : null;

            if (onMoreClick) {
                return;
            }
            setItemQuery({
                id: param.id,
                // eslint-disable-next-line camelcase
                sf_ch: param.sf_ch,
                moduleId: infos.moduleId,
                preview: param.preview,
                pageSize: 20,
                pageNo: 1,
            });
        },
        []
    );

    const loadMore = useCallback(
        () => {
            if (onMoreClick) {
                onMoreClick();
                return;
            }
            const query = {
                // eslint-disable-next-line camelcase
                sf_ch: itemQuery?.sf_ch,
                id: param.id,
                pageNo: itemQuery.pageNo + 1,
                pageSize: itemQuery.pageSize,
                moduleId: infos.moduleId,
            };
            setItemQuery(query);
        },
        []
    );

    const handleMoreClick = useCallback(
        infos => {
            const dataSize = infos.itemList?.length || 0;
            const nextMaxShowSize = sizeRef.current + 5;
            const value = dataSize > nextMaxShowSize ? nextMaxShowSize : dataSize;

            !(dataSize < infos.count && dataSize === size) ? setSize(value) : null;
            dataSize < infos.count && dataSize === sizeRef.current ? loadMore() : sizeRef.current = value;
        },
        []
    );


    const fetchItemList = async (
        id?: string, moduleId?: number, pageSize?: number,
        // eslint-disable-next-line camelcase
        pageNo?: number, sf_ch?: string, preview?: boolean
    ) => {
        if (!id || !moduleId || onMoreClick) {
            return;
        }
        try {
            const data = await getTopicItemlist({
                id: id,
                moduleId: moduleId,
                pageSize: pageSize || 20,
                pageNo: pageNo || 1,
                // eslint-disable-next-line camelcase
                sf_ch: sf_ch,
                preview: preview,
            });

            const list = [];
            if (module?.itemList && Array.isArray(module?.itemList)) {
                list.push(...module.itemList);
            }
            if (data && Array.isArray(data.list)) {
                list.push(...data.list);
            }

            const appendModule = {
                moduleId: module?.moduleId,
                channelId: module?.channelId,
                moduleName: module?.moduleName,
                isShowTitle: module?.isShowTitle,
                moduleMaxRows: module?.moduleMaxRows,
                moduleEnName: module?.moduleEnName,
                moduleSubTitle: module?.moduleSubTitle,
                moduleMaxColumns: module?.moduleMaxColumns,
                moduleType: module?.moduleType,
                moduleStyle: module?.moduleStyle,
                count: data.count,
                moreUrl: module?.moreUrl,
                itemList: pageNo === 1 ? data.list : list,
            };

            setModule(appendModule);

            // eslint-disable-next-line no-unused-expressions, no-negated-condition
            pageNo !== 1 ? handleMoreClick(appendModule) : null;

        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    useEffect(
        () => {
            fetchItemList(itemQuery.id,
                itemQuery.moduleId,
                itemQuery.pageSize,
                itemQuery.pageNo,
                itemQuery?.sf_ch,
                itemQuery.preview);
        },
        [itemQuery]
    );

    const {navigateToUrl} = useJumpUrl();

    const handleClick = useCallback(
        (linkUrl?: string, flowInfo?) => {
            const urlParams = {
                linkUrl,
            };
            const logParams = {
                page: page,
                value: 'jump_neirong_danliedatu',
                channelId: infos?.channelId,
                pageType: channelBusinessLine,
                moduleId: infos?.moduleId,
                moduleName: infos?.moduleName,
                customize: {
                    // eslint-disable-next-line camelcase
                    value_type: VALUE_TYPE.ITEM,
                    // eslint-disable-next-line camelcase
                    value_id: flowInfo?.itemId,
                    // eslint-disable-next-line camelcase
                    value_title: flowInfo?.title,
                },
            };
            navigateToUrl(urlParams, logParams);
        },
        [channelBusinessLine, infos.channelId, infos.moduleId, infos.moduleName, page, navigateToUrl]
    );

    const hasTitle = useMemo(
        () => {
            return infos.isShowTitle === 1 && (infos.moduleName || infos.moduleSubTitle);
        },
        [infos.isShowTitle, infos.moduleName, infos.moduleSubTitle]
    );

    return (

        <div
            data-show={JSON.stringify({
                'page': page,
                'value': 'jump_neirong_danliedatu',
                'pageSourceId': infos.channelId,
                'pageType': channelBusinessLine,
                'valueId': infos.moduleId,
                'valueTitle': infos.moduleName,
                'valueType': VALUE_TYPE.MODULE,
            })}
        >
            {
                (infos.isShowTitle === 1 && (infos.moduleName || infos.moduleSubTitle))
                    ? (
                        <div className={styles.titleParent}>
                            <div className={styles.titleContent}>
                                {
                                    infos.moduleName
                                        ? (
                                            <span className={styles(styles.title, 'c-line-clamp1')}>
                                                {infos.moduleName}
                                            </span>
                                        )
                                        : null
                                }
                                {
                                    infos.moduleSubTitle
                                        ? (
                                            <span className={styles.subTitle}>
                                                {infos.moduleSubTitle}
                                            </span>
                                        )
                                        : null
                                }
                                <div>
                                    {
                                        infos?.moreUrl
                                            ? (
                                                <img
                                                    src={'https://med-fe.cdn.bcebos.com/content-external-channel/rightDirect.png'}
                                                    className={styles.rightMoreArrow}
                                                    onClick={() => {
                                                        handleClick(infos.moreUrl);
                                                    }}
                                                />
                                            )
                                            : null
                                    }
                                </div>
                            </div>
                        </div>
                    )
                    : null
            }

            {
                infos.itemList?.map(
                    (flowInfo, index) => (
                        <div key={flowInfo.itemId}>
                            {
                                index < size ? (
                                    <div
                                        onClick={() => handleClick(flowInfo.fieldValue?.link, flowInfo)}
                                        data-show={JSON.stringify({
                                            'page': page,
                                            'value': 'jump_neirong_danliedatu',
                                            'pageSourceId': flowInfo.channelId,
                                            'pageType': channelBusinessLine,
                                            'valueId': flowInfo.itemId,
                                            'valueTitle': flowInfo.title,
                                            'valueType': VALUE_TYPE.ITEM,
                                        })}
                                    >
                                        <div className={cx(styles.bigImgCardParent,
                                            (!hasTitle && index === 0) ? styles.bigImgCardParentNoTitle : ''
                                        )}
                                        >
                                            <img
                                                src={flowInfo.fieldValue?.image}
                                                className={styles.bigImgCard}
                                            />
                                            {
                                                flowInfo.fieldValue?.type === 2
                                                    ? <div className={styles.mask} />
                                                    : null
                                            }
                                            {
                                                flowInfo.fieldValue?.type === 2
                                                    ? (
                                                        <img
                                                            className={styles.playIcon}
                                                            preview=''
                                                            src={'https://med-fe.cdn.bcebos.com/content-external-channel/play.png'}
                                                        />
                                                    )
                                                    : null
                                            }
                                            {
                                                (flowInfo.fieldValue?.type === 2 && flowInfo.fieldValue?.duration)
                                                    ? (
                                                        <div className={styles.videoTime}>
                                                            {flowInfo.fieldValue?.duration}
                                                        </div>
                                                    )
                                                    : null
                                            }
                                        </div>

                                        <div
                                            className={cx(styles.bottomTitle,
                                                {[styles.bottomTitleTwo]: flowInfo.fieldValue?.titleLine !== 1}
                                            )}
                                            style={flowInfo.fieldValue?.titleLine
                                                ? {WebkitLineClamp: flowInfo.fieldValue?.titleLine}
                                                : {}
                                            }
                                        >
                                            {
                                                flowInfo.fieldValue?.title
                                            }
                                        </div>

                                        {
                                            flowInfo.fieldValue?.expertPortrait || flowInfo.fieldValue?.expertName
                                        || flowInfo.fieldValue?.expertTitle || flowInfo.fieldValue?.expertHospital
                                        || flowInfo.fieldValue?.hospitalLevel
                                                ? (
                                                    <div className={styles.bottomContent}>
                                                        {
                                                            flowInfo.fieldValue?.expertPortrait
                                                                ? (
                                                                    <img
                                                                        className={styles.header}
                                                                        onClick={() => {}}
                                                                        src={flowInfo.fieldValue.expertPortrait}
                                                                    />
                                                                )
                                                                : null
                                                        }

                                                        <span className={styles.des}>
                                                            {flowInfo.fieldValue?.expertName}
                                                            {flowInfo.fieldValue?.expertTitle}
                                                            {flowInfo.fieldValue?.expertHospital}
                                                        </span>

                                                        {
                                                            flowInfo.fieldValue?.hospitalLevel
                                                                ? (
                                                                    <div
                                                                        className={styles.tagParent}
                                                                    >
                                                                        <span
                                                                            className={styles.tag}
                                                                        >
                                                                            {flowInfo.fieldValue?.hospitalLevel}
                                                                        </span>
                                                                    </div>
                                                                )
                                                                : null
                                                        }

                                                    </div>
                                                )
                                                : null
                                        }

                                        {
                                            index < (infos.count || 1) - 1 ? <div className={styles.line} /> : null
                                        }

                                    </div>
                                )
                                    : null
                            }

                        </div>
                    )
                )
            }

            {
                // eslint-disable-next-line max-len
                (infos.itemList && ((infos.itemList.length || 0) > size && size > 0 || infos.itemList.length < (infos.count || 0)))
                    ? (
                        <div
                            className={styles.moreContent}
                            onClick={() => handleMoreClick(infos)}
                        >

                            <span
                                className={styles.moreText}
                            >
                                展开更多
                            </span>

                            <img
                                src={'https://med-fe.cdn.bcebos.com/content-external-channel/more-down.png'}
                                className={styles.moreArrow}
                            />
                        </div>
                    )
                    : null
            }

        </div>
    );
};

export default SelfTopicContentFlowBig;
